/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Badge, Loader } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import { TFunction } from '@scuf/localization/dist/types';
import React, { useContext, useEffect, useState } from 'react';
import descriptors from '../../../../../../locales/en-US/descriptors';
import { IAny } from '../../../../../DashboardKPIWidgets/IEnergyManagementProps';
import CommonProgressBar from '../../../../../shared/CommonProgressBar/CommonProgressBar';
import { IDataTableColumn } from '../../../../../shared/MeterSavingsDetailTable/IMeterSavingsDetailTableProps';
import TruncateText from '../../../../../shared/TruncatedText/TruncateText';
import {
  ElEMENT_PER_SCROLL,
  EnergyViewDetailsAllUtilitiesFields,
  KPI_VALUE_DECIMAL_PRECISION,
  MAGIC_VALUE,
  PERCENT_CHANGE_THRESHOLD
} from '../../../../../shared/Utils/Constants';
import {
  getConvertedUnit,
  getConvertedValues,
  getNullConverted
} from '../../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { ColumnDetail } from '../../../../EnergyUsagesViewDetailsWidget/AllUtilitiesDetails/IEnergyUsagesBreakdownProps';
import {
  IPortfolioTableColumns,
  baselineFields,
  consuptionFields
} from '../../IPortfolioEnergyViewDetails';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../../../../shared/Utils/unitHandling/unitHandling';
import {
  handleUnitConversion,
  maxConsupValueCheck,
  maxEUIValCheck,
  maxEuiValueCheck,
  maxValBDBSCheck
} from '../../../../../shared/Utils/Utils';
import { ApiContext } from '../../../../../../context/ApiContext';
import { EnergyUsageField } from '../../../../../shared/Enums';
const {
  actualConsup,
  BaselineConsup,
  consupDeviation,
  previousPeriodConsumption,
  changePercent,
  displayUnit,
  euiCurrent,
  euiPrevPeriod,
  EuiChange,
  EuiChangePercentage,
  sites,
  absoluteTotalConsumption,
  absoluteTotalBaselineConsumption
} = EnergyViewDetailsAllUtilitiesFields;
export const renderSiteName = (cellData: ICellData) => {
  const { value, rowData } = cellData;
  return (
    <div
      className="savings-badge portfolio-navigation-link"
      onClick={() => {
        rowData.onSiteclick(rowData.id);
      }}
    >
      {
        <span
          style={{
            visibility: rowData.totalEnergyDeviation < 0 ? 'visible' : 'hidden'
          }}
          className="portfolio-vd-badge"
        >
          <Badge color="red" empty />
        </span>
      }
      <TruncateText text={value} />
    </div>
  );
};

export const renderConsupProgressBar = (
  cellData: ICellData,
  sourceType: string,
  originalUnit: string,
  fromUnit: string
) => {
  const { value, rowData } = cellData;
  let toUnitActualConsumption = getMaxConvertedValueFromArray(
    sourceType,
    sourceType.toLowerCase() === 'gas' ? originalUnit : fromUnit,
    [rowData.totalConsumption],
    originalUnit
  ).Unit;
  const actual: IAny = getConvertedValue(
    sourceType,
    sourceType.toLowerCase() === 'gas' ? originalUnit : fromUnit,
    rowData[absoluteTotalConsumption],
    toUnitActualConsumption
  ).Value;
  const previousPeriod: IAny = getConvertedValue(
    sourceType,
    sourceType.toLowerCase() === 'gas' ? originalUnit : fromUnit,
    rowData[absoluteTotalBaselineConsumption],
    toUnitActualConsumption
  ).Value;
  const saving = rowData[consupDeviation];
  const savingsPercentage = rowData[changePercent];
  //  const relativeProgressWidth = rowData.relativeProgressWidth;
  let actualFieldForProgressbar: consuptionFields = 'totalConsumption',
    baselineFieldForProgressbar: baselineFields = 'totalBaselineConsumption';
  let maxConsupValueforProgressBar: number | null = Number.MIN_VALUE;
  let maxEuiValueforProgressBar: number | null = Number.MIN_VALUE;
  const maxVal = maxValBDBSCheck(
    rowData,
    actualFieldForProgressbar,
    baselineFieldForProgressbar
  );

  maxConsupValueforProgressBar = maxConsupValueCheck(
    maxVal,
    maxConsupValueforProgressBar
  );

  maxConsupValueforProgressBar = handleUnitConversion(
    maxConsupValueforProgressBar,
    sourceType,
    originalUnit,
    toUnitActualConsumption
  );

  if (
    rowData.actualEui !== null &&
    rowData.baselineEui !== null &&
    maxEuiValueforProgressBar !== null
  ) {
    const maxEUIVal = maxEUIValCheck(rowData.actualEui, rowData.baselineEui);
    maxEuiValueforProgressBar = maxEuiValueCheck(
      maxEUIVal,
      maxEuiValueforProgressBar
    );
  }
  let relativeProgressWidth: IAny = maxConsupValueforProgressBar;
  return (
    <span className="score-category-meterdata actual-consup">
      <span className="score-category-consup">
        {/* {getConsupWithUnit(value, rowData[displayUnit])} */}
        {isNaN(Number(rowData.totalConsumption)) ||
        rowData.totalConsumption === '' ||
        rowData.totalConsumption === null ||
        rowData.totalConsumption === 0
          ? '--'
          : `${
              getConvertedValue(
                sourceType,
                originalUnit,
                rowData.totalConsumption,
                rowData.actualUnit
              ).Value
            } ${rowData.actualUnit}`}
      </span>
      <span className="score-category-progress">
        {getProgressBar(
          actual,
          previousPeriod,
          saving,
          savingsPercentage,
          relativeProgressWidth
        )}
      </span>
    </span>
  );
};

export const renderBaselineConsup = (
  cellData: ICellData,
  sourceType: string,
  originalUnit: string,
  fromUnit: string
) => {
  const { value, rowData } = cellData;
  let toUnibaselineConsumption = getMaxConvertedValueFromArray(
    sourceType,
    fromUnit,
    [rowData.totalBaselineConsumption],
    originalUnit
  ).Unit;
  return (
    <span className="score-category-meterdata">
      <span className="score-category-percent">
        {/* <div className="measurementunit">
          {getNullConverted(value)}
        </div> */}
        {/* {getConvertedUnit(value,rowData[displayUnit])} */}
        {isNaN(Number(rowData.totalBaselineConsumption)) ||
        rowData.totalBaselineConsumption === '' ||
        rowData.totalBaselineConsumption === null ||
        rowData.totalBaselineConsumption === 0
          ? '--'
          : `${
              getConvertedValue(
                sourceType,
                originalUnit,
                rowData.totalBaselineConsumption,
                rowData.baselineUnit
              ).Value
            } ${rowData.baselineUnit}`}
      </span>
    </span>
  );
};

export const renderConsupChange = (
  cellData: ICellData,
  sourceType: string,
  originalUnit: string,
  fromUnit: string,
  showChangeWhenData: boolean = false
) => {
  const { value, rowData } = cellData;
  let converToUnit = getMaxConvertedValueFromArray(
    sourceType,
    sourceType.toLowerCase() === 'gas' ? originalUnit : fromUnit,
    [rowData.totalEnergyDeviation],
    originalUnit
  ).Unit;
  const hasBaseline =
    !isNaN(Number(rowData.totalBaselineConsumption)) &&
    !isNaN(Number(rowData.totalConsumption)) &&
    Number(rowData.totalBaselineConsumption) !== 0 &&
    Number(rowData.totalConsumption) !== 0;
  const percentageChange = getConvertedValues(rowData[changePercent]);
  let totalEnergyDeviation =
    hasBaseline &&
    Math.abs(Number(percentageChange)) <= PERCENT_CHANGE_THRESHOLD
      ? getConvertedValue(
          sourceType,
          originalUnit,
          rowData.totalEnergyDeviation,
          rowData.changeUnit
        ).Value
      : showChangeWhenData
      ? null
      : getConvertedValue(
          sourceType,
          originalUnit,
          rowData.totalEnergyDeviation,
          rowData.changeUnit
        ).Value;
  let convertdeviationScaledData: IAny =
    rowData.totalEnergyDeviation !== 0 &&
    getConvertedValue(
      sourceType,
      originalUnit,
      rowData.totalEnergyDeviation,
      rowData.changeUnit
    ).Value;
  const valueWithUnit = `${getConvertedValues(
    totalEnergyDeviation,
    convertdeviationScaledData
  )} ${rowData.changeUnit}`;
  const formattedPercenatge = ` (${getConvertedValues(
    rowData[changePercent]
  )}%)`;
  return totalEnergyDeviation !== null ? (
    <span className="score-category-meterdata-meter">
      <span style={{ color: getPercentageColor(rowData[changePercent]) }}>
        {valueWithUnit + formattedPercenatge}
      </span>
    </span>
  ) : (
    <span className="score-category-meterdata-meter">--</span>
  );
};

export const renderEUIProgressBar = (cellData: ICellData) => {
  const { value, rowData } = cellData;

  const actual = rowData[euiCurrent];
  const previousPeriod = rowData[euiPrevPeriod];
  const saving = rowData[EuiChange];
  const savingsPercentage = rowData[EuiChangePercentage];
  const progessBarRelativeWidth = rowData.relativeEuiWidth;
  return (
    <span className="score-category-meterdata actual-consup">
      <span className="score-category-consup">
        {getEUIConsupWithUnit(value)}
      </span>
      <span className="score-category-progress">
        {getProgressBar(
          actual,
          previousPeriod,
          saving,
          savingsPercentage,
          progessBarRelativeWidth
        )}
      </span>
    </span>
  );
};
export const getConsupWithUnit = (value: number, displayUnitValue: string) => {
  return (
    <>
      <span className="measurementunit">{getNullConverted(value)}</span>
      {getConvertedUnit(value, displayUnitValue)}
    </>
  );
};
export const getEUIConsupWithUnit = (value: number) => {
  return (
    <>
      <span className="measurementunit">
        {value ? value.toFixed(KPI_VALUE_DECIMAL_PRECISION) : '--'}
      </span>
    </>
  );
};

export const getProgressBar = (
  actual: number,
  previousPeriod: number,
  saving: number,
  savingsPercentage: number,
  progessBarRelativeWidth?: number
) => {
  return (
    <CommonProgressBar
      actual={actual}
      previousPeriod={previousPeriod}
      savings={saving}
      savingsPercentage={savingsPercentage}
      progessBarRelativeWidth={progessBarRelativeWidth}
    />
  );
};
export const getPercentageColor = (value: number) => {
  if (value > 0) return '#7eb338';
  else if (value < 0) return '#f15a4f';
  else return '#b0b0b0';
};

export const EUIPreviousPeriod = (cellData: ICellData) => {
  const { value } = cellData;
  return (
    <span className="all-utility-prevperiod">
      {value ? value.toFixed(KPI_VALUE_DECIMAL_PRECISION) : '--'}
    </span>
  );
};

export const renderEUIChange = (
  cellData: ICellData,
  showChangeWhenData: boolean = false
) => {
  const { value, rowData } = cellData;
  // const apiContext = useContext(ApiContext);
  const valueWithUnit = `${getConvertedValues(value)} ${getConvertedUnit(
    value,
    ''
  )}`;
  const formattedPercenatge = ` (${getConvertedValues(
    rowData[EuiChangePercentage]
  )}%)`;
  const euiPercent = getConvertedValues(rowData[EuiChangePercentage]);
  return value !== null &&
    (Math.abs(Number(euiPercent)) <= PERCENT_CHANGE_THRESHOLD ||
      !showChangeWhenData) ? (
    <span className="score-category-meterdata-meter">
      <span style={{ color: getPercentageColor(rowData[EuiChangePercentage]) }}>
        {valueWithUnit + formattedPercenatge}
      </span>
    </span>
  ) : (
    <span className="score-category-meterdata-meter">--</span>
  );
};
export const getColumnDetails = (
  t: TFunction,
  tabName: string | undefined,
  sourceType: string,
  originalUnit: string,
  fromUnit: string,
  showChangeWhenData: boolean = false,
  subscriptionType: string
) => {
  const d = descriptors.DetailedView;
  const columnWidths = getColumnWidths(tabName);
  const columnDetails: Array<IPortfolioTableColumns> = [
    {
      className: 'datatable-column-meter',
      field: sites,
      header: t(d.SITES),
      sortable: true,
      initialWidth: columnWidths[MAGIC_VALUE.ZERO],
      render: renderSiteName
    },
    {
      className: 'datatable-column-actualConsumption',
      field: actualConsup,
      header: t(d.ACTUALCONS),
      sortable: true,
      initialWidth: columnWidths[MAGIC_VALUE.ONE],
      render: (rowData) =>
        renderConsupProgressBar(rowData, sourceType, originalUnit, fromUnit) // renderConsupProgressBar
    },
    {
      className: 'datatable-column-baselineConsumption',
      field: BaselineConsup,
      header:
        sourceType.toLowerCase() === 'water'
          ? t(d.PORTFOLIOPREVIOUSConsumption)
          : subscriptionType === EnergyUsageField.BASICSUBS
          ? t(d.PREVPERIOD)
          : t(d.BASELINECONSP),
      sortable: true,
      initialWidth: columnWidths[MAGIC_VALUE.TWO],
      render: (rowData) =>
        renderBaselineConsup(rowData, sourceType, originalUnit, fromUnit) //renderBaselineConsup
    },
    {
      className: 'datatable-column-change',
      field: consupDeviation,
      header: t(d.CHANGE), //sourceType.toLowerCase() === 'water' ? t(d.CHANGE) : t(d.CHANGEPERCENT),
      sortable: true,
      initialWidth: columnWidths[MAGIC_VALUE.THREE],
      render: (rowData) =>
        renderConsupChange(
          rowData,
          sourceType,
          originalUnit,
          fromUnit,
          showChangeWhenData
        )
    }
  ];
  if (tabName == 'allutilities') {
    columnDetails.push(
      {
        className: 'datatable-column-change',
        field: euiCurrent,
        header: t(d.EUICURRENT),
        sortable: true,
        initialWidth: columnWidths[MAGIC_VALUE.FOUR],
        // renderTempEUIColumns
        render: renderEUIProgressBar
      },
      {
        className: 'datatable-column-change',
        field: euiPrevPeriod,
        header: t(d.EUIPRVPERIOD),
        sortable: true,
        initialWidth: columnWidths[MAGIC_VALUE.FIVE],
        // comment for below  renderTempEUIColumns
        render: EUIPreviousPeriod
      },
      {
        className: 'datatable-column-change',
        field: EuiChange,
        header: t(d.CHANGE),
        sortable: true,
        initialWidth: columnWidths[MAGIC_VALUE.SIX],
        // comment for below renderTempEUIColumns
        render: (cellData: IAny) => {
          return renderEUIChange(cellData, showChangeWhenData);
        }
      }
    );
  }
  return columnDetails;
};

const getColumnWidths = (tabName: string | undefined) => {
  if (tabName == 'allutilities')
    return ['15%', '17%', '14%', '12%', '17%', '16%', '10%'];
  else return ['30%', '50%', '20%', '20%'];
};

export const Fivek = (props: any) => {
  const {
    data,
    columnDetails,
    rowcellDataRenderer,
    nilmCheck
    // handleVirtualScroll
  } = props;

  const [uMeters, setUMeters] = useState<IAny>(data.utilityMeters);
  const [totalItem, setTotalItem] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const getInitialMeters = async (data: IAny) => {
    let endIndex: number;
    if (data.utilityMeters.length > ElEMENT_PER_SCROLL) {
      endIndex = ElEMENT_PER_SCROLL;
    } else {
      endIndex = data.utilityMeters.length;
    }
    const tempUmeters = await props.getNextData(0, endIndex, data.utilityType);
    setUMeters(tempUmeters);
    setIsLoading(false);
  };

  useEffect(() => {
    const fileteredDynamicData = props.dynamicMeterData.filter(
      (item: any) => item.id === data.id
    )[0];

    if (data.utilityMeters) {
      if (
        fileteredDynamicData?.utilityMeters?.length &&
        fileteredDynamicData.utilityMeters[0] &&
        !isNaN(fileteredDynamicData.utilityMeters[0].totalActualEmission)
        // &&
        // data.utilityMeters[ElEMENT_PER_SCROLL - 1].totalActualEmission
      ) {
        setUMeters(
          fileteredDynamicData.utilityMeters.slice(0, ElEMENT_PER_SCROLL)
        );
        // setUMeters(fileteredDynamicData.utilityMeters.slice(0, Math.ceil(data?.utilityMeters?.length / 2)));
      } else {
        setIsLoading(true);
        getInitialMeters(data);
      }

      // setTotalItem(data.totalItems);
    }
  }, []);

  const handleVirtualScroll = async ({ first, rows }: any) => {
    let last = first + rows;
    if (last >= data?.utilityMeters?.length) {
      last = data?.utilityMeters?.length;
    }
    const tempUmeters = await props.getNextData(first, last, data.utilityType);
    setUMeters(tempUmeters);
  };

  const debounce = (fn: Function, ms: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function(this: IAny, ...args: IAny[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
  };

  return (
    <div
      className="UtilityBill-row-expansion"
      style={isLoading ? { height: '14rem' } : {}}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <DataTable
          data={uMeters}
          showHeader={false}
          scrollable={true}
          lazy={true}
          virtualScroll={true}
          rows={props.NumberOfItemsPerPage}
          totalRecords={data?.utilityMeters?.length}
          onVirtualScroll={(e: { first: any; rows: any }) => {
            // debounce(()=> {handleVirtualScroll(e)}, 3000)();
            handleVirtualScroll && handleVirtualScroll(e);
          }}
          virtualRowHeight={50}
          scrollHeight="150px"
        >
          <DataTable.Column
            initialWidth={nilmCheck ? '0.6%' : '1.5%'}
            field=""
            header=""
          />
          {columnDetails.map((column: IDataTableColumn) => {
            return (
              <DataTable.Column
                key={column.field}
                className={column.className}
                field={column.field}
                header={column.header}
                sortable={column.sortable}
                renderer={rowcellDataRenderer}
                initialWidth={column.initialWidth}
              />
            );
          })}
        </DataTable>
      )}
    </div>
  );
};

export const rowExpansionTemplate = (
  data: IAny,
  columnDetails: ColumnDetail[],
  rowcellDataRenderer: (cellData: ICellData) => JSX.Element,
  nilmCheck?: boolean
) => {
  return (
    <div className="UtilityBill-row-expansion">
      <DataTable
        data={data.utilityMeters}
        showHeader={false}
        scrollable={true}
        scrollHeight="20rem"
      >
        <DataTable.Column
          initialWidth={nilmCheck ? '0.6%' : '1.5%'}
          field=""
          header=""
        />
        {columnDetails.map((column: IDataTableColumn) => {
          return (
            <DataTable.Column
              key={column.field}
              className={column.className}
              field={column.field}
              header={column.header}
              sortable={column.sortable}
              renderer={rowcellDataRenderer}
              initialWidth={column.initialWidth}
            />
          );
        })}
      </DataTable>
    </div>
  );
};
